import { memo, useRef, useState, useEffect } from 'react'
import {
  Box,
  Checkbox,
  Radio,
  TextField,
  InputAdornment,
  Tooltip,
  Typography,
} from '@mui/material'
import { Virtuoso } from 'react-virtuoso'
import { StorePerformanceItem } from '../types'
import { SxProps, Theme } from '@mui/system'
import { IconName } from '../../../../../../../utils/icons'

interface SelectionListProps {
  title: string
  selectedCount: number
  selectedCountLabel?: string
  searchValue: string
  onSearchChange: (value: string) => void
  data: StorePerformanceItem[]
  selectedItems: StorePerformanceItem[]
  isSelected: (id: string) => boolean
  toggle: (item: StorePerformanceItem) => void
  showSelectAll: boolean
  areAllSelected: boolean
  toggleAll: () => void
  loading: boolean
  disabled: boolean
  emptyMessage: string
  searchPlaceholder: string
  selectAllLabel: string
  icons: (name: IconName) => React.ReactNode
  selectionType?: 'checkbox' | 'radio'
  styles: {
    listColumn: SxProps<Theme>
    listColumnHeader: SxProps<Theme>
    listColumnCounter: SxProps<Theme>
    searchField: SxProps<Theme>
    checkboxRow: SxProps<Theme>
    checkboxRowAll: SxProps<Theme>
  }
  colors: { color9: string; color13: string }
  t: (key: string, options?: Record<string, unknown>) => string
}

const VirtuosoList = memo(function VirtuosoList({
  data,
  isSelected,
  toggle,
  showSelectAll,
  areAllSelected,
  toggleAll,
  selectAllLabel,
  styles,
  colors,
  t,
  scrollParentRef,
  selectionType,
}: {
  data: StorePerformanceItem[]
  isSelected: (id: string) => boolean
  toggle: (item: StorePerformanceItem) => void
  showSelectAll: boolean
  areAllSelected: boolean
  toggleAll: () => void
  selectAllLabel: string
  styles: { checkboxRow: SxProps<Theme>; checkboxRowAll: SxProps<Theme> }
  colors: { color9: string }
  t: (key: string) => string
  scrollParentRef: React.RefObject<HTMLDivElement> | null
  selectionType: 'checkbox' | 'radio'
}) {
  return (
    <Virtuoso
      customScrollParent={scrollParentRef?.current || undefined}
      style={{ height: '100%' }}
      totalCount={data.length}
      components={{
        EmptyPlaceholder: () => (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '13px', color: colors.color9 }}>
              {t('general.messages.emptyTable')}
            </Typography>
          </Box>
        ),
        Header: () => {
          if (selectionType === 'radio' || !showSelectAll) return null
          return (
            <Box
              sx={styles.checkboxRowAll}
              onClick={toggleAll}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  toggleAll()
                }
              }}
            >
              <Checkbox
                size="small"
                checked={areAllSelected}
                onChange={toggleAll}
                onClick={(e) => e.stopPropagation()}
                tabIndex={-1}
              />
              <Tooltip title={selectAllLabel} placement="top" enterDelay={500}>
                <Typography noWrap sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {selectAllLabel}
                </Typography>
              </Tooltip>
            </Box>
          )
        },
      }}
      itemContent={(index) => {
        const item = data[index]
        const checked = isSelected(item.id)
        const SelectionControl = selectionType === 'radio' ? Radio : Checkbox
        const isRadio = selectionType === 'radio'
        return (
          <Box
            sx={{
              ...styles.checkboxRow,
              ...(isRadio ? { height: '50px' } : {}),
            }}
            onClick={() => toggle(item)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                toggle(item)
              }
            }}
            aria-label={`${item.label} ${checked ? t('general.selected') : t('general.notSelected')}`}
          >
            <SelectionControl
              size={isRadio ? 'medium' : 'small'}
              checked={checked}
              onChange={() => toggle(item)}
              onClick={(e) => e.stopPropagation()}
              tabIndex={-1}
              sx={isRadio ? {
                padding: '9px',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.5rem',
                },
              } : undefined}
            />
            <Tooltip title={item.label} placement="top" enterDelay={500}>
              <Typography noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {item.label}
              </Typography>
            </Tooltip>
          </Box>
        )
      }}
    />
  )
})

const SelectionList = ({
  title,
  selectedCount,
  selectedCountLabel,
  searchValue,
  onSearchChange,
  data,
  isSelected,
  toggle,
  showSelectAll,
  areAllSelected,
  toggleAll,
  loading,
  disabled,
  emptyMessage,
  searchPlaceholder,
  selectAllLabel,
  icons,
  selectionType = 'checkbox',
  styles,
  colors,
  t,
}: SelectionListProps) => {
  const scrollParentRef = useRef<HTMLDivElement>(null)
  const [scrollParentReady, setScrollParentReady] = useState(false)

  useEffect(() => {
    if (scrollParentRef.current) {
      setScrollParentReady(true)
    }
  }, [])

  useEffect(() => {
    // Resetear y re-establecer cuando cambian los datos
    setScrollParentReady(false)
    const timer = setTimeout(() => {
      if (scrollParentRef.current) {
        setScrollParentReady(true)
      }
    }, 0)
    return () => clearTimeout(timer)
  }, [data])

  return (
    <Box sx={styles.listColumn}>
      <Box sx={styles.listColumnHeader}>
        <Typography>{title}</Typography>
        {selectedCount > 0 && (
          <Typography sx={styles.listColumnCounter}>
            {selectedCountLabel || selectedCount}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          px: '16px',
          pb: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          flex: 1,
          minHeight: 0,
        }}
      >
        {disabled ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: '13px',
                color: colors.color9,
                textAlign: 'center',
              }}
            >
              {emptyMessage}
            </Typography>
          </Box>
        ) : loading ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            <Typography sx={{ fontSize: '13px', color: colors.color9 }}>
              {t('general.loading')}
            </Typography>
          </Box>
        ) : (
          <Box ref={scrollParentRef} sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: '#fff',
                pb: '4px',
              }}
            >
              <TextField
                placeholder={searchPlaceholder}
                size="small"
                fullWidth
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                sx={styles.searchField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {icons('search')}
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              {scrollParentReady && (
                <VirtuosoList
                  key={`virtuoso-${data.length}-${scrollParentReady}`}
                  data={data}
                  isSelected={isSelected}
                  toggle={toggle}
                  showSelectAll={showSelectAll}
                  areAllSelected={areAllSelected}
                  toggleAll={toggleAll}
                  selectAllLabel={selectAllLabel}
                  scrollParentRef={scrollParentRef}
                  selectionType={selectionType}
                  styles={{
                    checkboxRow: styles.checkboxRow,
                    checkboxRowAll: styles.checkboxRowAll,
                  }}
                  colors={{ color9: colors.color9 }}
                  t={t}
                />
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export { SelectionList }
